/**
 * `/studio` — public landing for the Studio tier (moat plan §5.3/§5.4).
 * Landing-styled (`.mk`, light-only, no shadcn in here); the form posts to `/api/public/studio-waitlist`.
 */
import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { Container, FeatureCol, MkAnchor, MkFooter, MkLink, MkNav, Overline, SectionHead } from '#/components/landing/mk'
import { ApiError } from '#/lib/api'
import { joinStudioWaitlist, SEGMENTS, type WaitlistSegment } from '#/lib/api-studio'
import { m } from '#/paraglide/messages'
import { getLocale } from '#/paraglide/runtime'

export const Route = createFileRoute('/studio')({
  head: () => ({
    meta: [
      { title: m.st_meta_title() },
      { name: 'description', content: m.st_meta_desc() },
      { property: 'og:title', content: m.st_meta_title() },
      { property: 'og:description', content: m.st_meta_desc() },
    ],
  }),
  component: StudioLanding,
})

const SEG_LABEL: Record<WaitlistSegment, () => string> = {
  agency: () => m.st_seg_agency(),
  freelancer: () => m.st_seg_freelancer(),
  campus: () => m.st_seg_campus(),
  other: () => m.st_seg_other(),
}

const inputCls = 'h-11 w-full rounded-lg border border-[var(--mk-border)] bg-white px-3 text-[15px] text-[var(--mk-fg)] outline-none placeholder:text-[var(--mk-fg-muted)] focus:border-[var(--mk-fg)]'

function StudioLanding() {
  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main>
        <section className="pt-16 pb-10 lg:pt-24">
          <Container className="flex flex-col items-center gap-6 text-center">
            <Overline>{m.st_hero_overline()}</Overline>
            <h1 className="max-w-[20ch]">{m.st_hero_title()}</h1>
            <p className="mk-lead max-w-[56ch]">{m.st_hero_sub()}</p>
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              <MkAnchor href="#waitlist">{m.st_hero_cta()}</MkAnchor>
              <MkLink to="/pricing" search={{ voucher: undefined }} variant="secondary" arrow>
                {m.st_hero_cta2()}
              </MkLink>
            </div>
          </Container>
        </section>

        <section className="py-14 lg:py-20">
          <Container className="flex flex-col gap-12">
            <SectionHead title={m.st_val_title()} lead={m.st_price_line()} />
            <div className="grid gap-10 sm:grid-cols-3">
              <FeatureCol title={m.st_val1_title()}>{m.st_val1_desc()}</FeatureCol>
              <FeatureCol title={m.st_val2_title()}>{m.st_val2_desc()}</FeatureCol>
              <FeatureCol title={m.st_val3_title()}>{m.st_val3_desc()}</FeatureCol>
            </div>
          </Container>
        </section>

        <section className="pb-16">
          <Container className="flex flex-col gap-8">
            <SectionHead align="left" title={m.st_mock_title()} lead={m.st_mock_desc()} />
            <ClientMock />
          </Container>
        </section>

        <section id="waitlist" className="scroll-mt-20 py-16 lg:py-20">
          <Container className="flex flex-col gap-10">
            <SectionHead title={m.st_wl_title()} lead={m.st_wl_desc()} />
            <WaitlistForm />
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}

/** Static preview of `/client/:planId` — plain Tailwind, no live data. */
function ClientMock() {
  const bars = [
    { name: m.st_mock_feature1(), verified: 100, override: 0, todo: 0 },
    { name: m.st_mock_feature2(), verified: 72, override: 14, todo: 14 },
    { name: m.st_mock_feature3(), verified: 40, override: 0, todo: 60 },
  ]
  return (
    <div className="mk-card-white overflow-hidden p-0">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--mk-border)] px-5 py-3">
        <div className="flex items-center gap-2.5">
          <span className="inline-block size-7 rounded-md bg-[#2757e6]" />
          <div className="leading-tight">
            <div className="text-[13px] font-semibold text-[#2757e6]">{m.st_mock_ws()}</div>
            <div className="text-[11px] text-[var(--mk-fg-muted)]">{m.st_mock_plan()}</div>
          </div>
        </div>
        <span className="text-[11px] text-[var(--mk-fg-faint)]">{m.st_cl_powered()}</span>
      </div>
      <div className="space-y-4 bg-[var(--mk-bg-muted)] p-5">
        <div className="rounded-xl border-2 border-[#2757e6] bg-white p-4">
          <div className="text-[15px] font-semibold">{m.st_mock_pending()}</div>
          <div className="mt-3 flex gap-2">
            <div className="h-9 flex-1 rounded-lg border border-[var(--mk-border)] bg-white px-3 text-[13px] leading-9 text-[var(--mk-fg-muted)]">{m.st_cl_approve_note()}</div>
            <div className="inline-flex h-9 items-center rounded-lg bg-[#2757e6] px-4 text-[13px] font-medium text-white">{m.st_mock_approve()}</div>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { l: m.st_mock_verified_label(), v: '24', c: '#16a34a' },
            { l: m.st_mock_rate_label(), v: '92%', c: undefined },
            { l: m.st_mock_exc_label(), v: '2', c: '#d97706' },
          ].map((s) => (
            <div key={s.l} className="rounded-xl border border-[var(--mk-border)] bg-white p-3">
              <div className="text-[11px] text-[var(--mk-fg-muted)]">{s.l}</div>
              <div className="text-xl font-semibold tabular-nums" style={s.c ? { color: s.c } : undefined}>
                {s.v}
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-[var(--mk-border)] bg-white p-4">
          <div className="text-[13px] font-semibold">{m.st_cl_phase({ n: 2 })}</div>
          <ul className="mt-3 space-y-3">
            {bars.map((b) => (
              <li key={b.name}>
                <div className="flex items-baseline justify-between text-[13px]">
                  <span>{b.name}</span>
                  <span className="text-[11px] text-[var(--mk-fg-muted)]">{b.verified}%</span>
                </div>
                <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-[#eef1f6]">
                  <span style={{ width: `${b.verified}%`, background: '#16a34a' }} />
                  <span style={{ width: `${b.override}%`, background: '#d97706' }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function WaitlistForm() {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [segment, setSegment] = useState<WaitlistSegment>('agency')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError(m.st_wl_err_email())
      return
    }
    setBusy(true)
    try {
      await joinStudioWaitlist({
        email: email.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(company.trim() ? { company: company.trim() } : {}),
        segment,
        ...(note.trim() ? { note: note.trim() } : {}),
        locale: getLocale() === 'id' ? 'id' : 'en',
      })
      setDone(true)
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined
      setError(code === 'rate_limited' ? m.st_wl_err_rate() : code === 'bad_request' ? m.st_wl_err_email() : m.st_wl_err_generic())
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="mk-card mx-auto flex w-full max-w-2xl flex-col items-center gap-2 p-8 text-center">
        <CheckCircle2 className="size-7 text-emerald-600" aria-hidden />
        <h3>{m.st_wl_success_title()}</h3>
        <p className="mk-muted">{m.st_wl_success_desc()}</p>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="mk-card mx-auto grid w-full max-w-2xl gap-4 p-7 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="st-wl-email" className="text-[13px] text-[var(--mk-fg-muted)]">
          {m.st_wl_email()}
        </label>
        <input id="st-wl-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="nama@studio.com" autoComplete="email" />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="st-wl-name" className="text-[13px] text-[var(--mk-fg-muted)]">
          {m.st_wl_name()}
        </label>
        <input id="st-wl-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} autoComplete="name" maxLength={120} />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="st-wl-company" className="text-[13px] text-[var(--mk-fg-muted)]">
          {m.st_wl_company()}
        </label>
        <input id="st-wl-company" value={company} onChange={(e) => setCompany(e.target.value)} className={inputCls} maxLength={120} />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="st-wl-segment" className="text-[13px] text-[var(--mk-fg-muted)]">
          {m.st_wl_segment()}
        </label>
        <select id="st-wl-segment" value={segment} onChange={(e) => setSegment(e.target.value as WaitlistSegment)} className={inputCls}>
          {SEGMENTS.map((s) => (
            <option key={s} value={s}>
              {SEG_LABEL[s]()}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5 sm:col-span-2">
        <label htmlFor="st-wl-note" className="text-[13px] text-[var(--mk-fg-muted)]">
          {m.st_wl_note()}
        </label>
        <textarea id="st-wl-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} className="w-full rounded-lg border border-[var(--mk-border)] bg-white p-3 text-[15px] text-[var(--mk-fg)] outline-none focus:border-[var(--mk-fg)]" />
      </div>
      {error ? <p className="text-[15px] text-red-600 sm:col-span-2">{error}</p> : null}
      <div className="sm:col-span-2">
        <button type="submit" className="mk-btn mk-btn-primary h-12" disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          {busy ? m.st_wl_sending() : m.st_wl_submit()}
        </button>
      </div>
    </form>
  )
}
